import type React from 'react';
import type { Color } from 'packages/ds/ColorPicker/types';
import { isGradientColor } from 'packages/pages/editor/RightSideMenu/FormComponents/ColorPicker/utils';

export type Props = {
    isMixed?: boolean;
    id: string;
    value: Color;
    label: string;
    onChange: (value: Color) => void;
    testId: string;
    isDisabled?: boolean;
    isGradientDisabled?: boolean;
    isLeftPositionAllowed?: boolean;
};

/** The subset of trigger props both the legacy popover and `ColorPickerV2` agree on. */
export type TriggerAriaProps = {
    'aria-haspopup': 'dialog';
    'aria-expanded': boolean;
    'aria-controls'?: string;
};

export type TriggerRenderProps = TriggerAriaProps & {
    isOpen: boolean;
    onClick: (event?: React.MouseEvent) => void;
    ref: React.Ref<HTMLDivElement>;
};

export function arePropsEqual(prevProps: Props, nextProps: Props) {
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);

    if (prevKeys.length !== nextKeys.length) {
        return false;
    }

    for (const key of prevKeys) {
        if (key === 'value' && typeof prevProps[key] !== 'string') {
            if (typeof prevProps[key] !== typeof nextProps[key]) {
                return false;
            }

            if (getColorType(prevProps[key]) !== getColorType(nextProps[key])) {
                return false;
            }

            if (getOpacity(prevProps[key]) !== getOpacity(nextProps[key])) {
                return false;
            }

            // Gradient comparing could be very heavy, so we just rerender it every time
            if (isGradientColor(nextProps[key])) {
                return false;
            } else if (getColorValue(prevProps[key]) !== getColorValue(nextProps[key])) {
                return false;
            }
        } else if (prevProps[key] !== nextProps[key]) {
            return false;
        }
    }

    return true;
}

function getColorType(color: Color) {
    if (typeof color === 'string') {
        return 'solid';
    }

    if ('type' in color) {
        return color.type;
    }

    return 'solid';
}

function getOpacity(color: Color) {
    if (typeof color === 'string') {
        return 100;
    }

    if ('opacity' in color) {
        return color.opacity;
    }

    return 100;
}

// @ts-ignore
function getColorValue(color: Color) {
    // @ts-ignore
    return 'color' in color ? color.color : color;
}
